import { FC, HTMLAttributes } from 'react'
import moment from 'moment'
import {
  AutocompleteOption,
  ConfiguredColumn,
  CurrentSortParams,
  EntityType,
  ObjectDataRecord,
  RestrictionDTO,
  SortDirection,
} from 'src/types'
import { Box, Grid } from '@mui/material'
import { GridArrowDownwardIcon, GridArrowUpwardIcon } from '@mui/x-data-grid'

import { MappedCheckbox } from '@components/MappedCheckbox'

import { SORT_DIRECTION } from '@hooks'
import {
  getTimeFromSeconds,
  isObjectValueType,
  renderBooleanValueCell,
  renderJSValueCell,
  renderJSXValueCell,
  renderObjectEmbeddedCell,
} from '@helpers'
import {
  BIND_TYPE,
  FIELD_VALUE_FORMAT,
  FIELD_VALUE_TYPE,
  MIN_AUTOCOMPLETE_POPPER_WIDTH,
} from '@constants'
import { RESTRICTION_TYPE } from '@types/restriction'

export type DropDownListLineProps = {
  dropDownList: EntityType | undefined
  combinedData: ObjectDataRecord[]
  option: AutocompleteOption
  liProps: HTMLAttributes<HTMLLIElement>
  columns: ObjectDataRecord[]
  currentSort: CurrentSortParams | null
  isDialogWindow: boolean
  pkFieldName?: string
  isLoading?: boolean
  showSortOrder: boolean
  onUpdateRestrictions: (restrictions: RestrictionDTO) => void
}

export type DropDownListHeadersProps = Pick<
  DropDownListLineProps,
  'columns' | 'onUpdateRestrictions' | 'isLoading' | 'showSortOrder'
>

const getColHeader = (
  title: string,
  code: string,
  currentSort: CurrentSortParams,
  sortKey: string,
  bindType: string,
  handleUpdateRestrictions: (restrictions: RestrictionDTO | null) => void,
  isLoading?: boolean,
  showSortOrder?: boolean
) => {
  const showDirection = currentSort?.sortDirection
  const isSortable = bindType !== BIND_TYPE.JSX && bindType !== BIND_TYPE.JS

  const handleHeaderClick = () => {
    if (!isSortable) return
    let nextDirection: SortDirection | null = SORT_DIRECTION.ASC

    if (currentSort?.sortDirection === SORT_DIRECTION.ASC) nextDirection = SORT_DIRECTION.DESC
    if (currentSort?.sortDirection === SORT_DIRECTION.DESC) nextDirection = null
    handleUpdateRestrictions({
      fieldName: title,
      fieldCode: code,
      restrictionType: RESTRICTION_TYPE.SORT,
      value: {
        columnToSort: sortKey,
        sortDirection: nextDirection || undefined,
        priority: currentSort.priority,
      },
    })
  }

  return (
    <Grid
      container
      item
      alignItems='center'
      justifyContent='flex-start'
      title={title}
      sx={{
        borderRight: `1px solid #303030`,
        flex: 1,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        minHeight: 25,
        backgroundColor: theme => theme.palette.background.headerTable,
        maxHeight: 52,
        px: '4px',
        pointerEvents: isLoading ? 'none' : 'auto',
        '&:last-child': {
          borderRight: `none`,
        },
      }}
      onClick={handleHeaderClick}
    >
      {title}
      {showDirection && isSortable && (
        <Box sx={{ display: 'flex' }}>
          {showDirection === SORT_DIRECTION.ASC ? (
            <GridArrowUpwardIcon />
          ) : (
            <GridArrowDownwardIcon />
          )}
          {showSortOrder && Number(currentSort.priority) + 1}
        </Box>
      )}
    </Grid>
  )
}

export const DropDownListHeaders: FC<DropDownListHeadersProps> = ({
  columns,
  isLoading,
  onUpdateRestrictions,
  showSortOrder,
}) => {
  return (
    <Grid
      sx={{
        display: 'block',
        marginTop: 0,
        padding: 0,
        minWidth: MIN_AUTOCOMPLETE_POPPER_WIDTH,
      }}
    >
      <Grid
        container
        flexWrap='nowrap'
        sx={{
          m: 0,
          p: 0,
          border: `1px solid #303030`,
          borderLeft: 'none',
          borderTop: 'none',
        }}
      >
        {columns.map(column => {
          return getColHeader(
            column.title,
            column.code,
            column.sort,
            column.sortKey,
            column.bindType,
            onUpdateRestrictions,
            isLoading,
            showSortOrder
          )
        })}
      </Grid>
    </Grid>
  )
}

export const DropDownListLine: FC<DropDownListLineProps> = ({
  onUpdateRestrictions: handleUpdateRestrictions,
  option,
  currentSort,
  isLoading,
  combinedData,
  liProps,
  columns,
  isDialogWindow,
  pkFieldName = '_id',
  showSortOrder,
}) => {
  const currentDataRowIndex = combinedData.findIndex(data => data[pkFieldName] === option.id)

  const renderValue = (column: ConfiguredColumn) => {
    const asCheckbox =
      column.valueFormat === FIELD_VALUE_FORMAT.NUMBER ||
      column.valueFormat === FIELD_VALUE_FORMAT.BOOLEAN

    if (
      (column.valueType === FIELD_VALUE_TYPE.DATETIME ||
        (column.pathArray?.length &&
          column.pathArray[column.pathArray.length - 1]?.valueType ===
            FIELD_VALUE_TYPE.DATETIME)) &&
      column.formatDate
    ) {
      if (!moment(combinedData[currentDataRowIndex][column.code]).isValid()) {
        return combinedData[currentDataRowIndex][column.code]
      }

      return moment(combinedData[currentDataRowIndex][column.code]).format(column.formatDate)
    }

    if (asCheckbox && column.valueFormat) {
      return (
        <Box sx={{ mt: 0.5 }}>
          <MappedCheckbox
            viewOnly
            valueFormat={column.valueFormat}
            outerValue={
              combinedData[currentDataRowIndex][column.code] as unknown as number | boolean
            }
          />
        </Box>
      )
    }

    if (column.valueFormat === FIELD_VALUE_FORMAT.TIME) {
      return getTimeFromSeconds(combinedData[currentDataRowIndex][column.code])
    }

    const srcObj = combinedData[currentDataRowIndex] || {}

    if (column.bindType === BIND_TYPE.JSX) {
      return renderJSXValueCell(column.value, srcObj)
    }

    if (column.bindType === BIND_TYPE.JS) {
      return renderJSValueCell(column.value, srcObj)
    }

    if (isObjectValueType(column.valueType) && column.objectValue) {
      return renderObjectEmbeddedCell(
        column.code,
        // column.objectValue,
        srcObj
      )
    }

    if (column.valueType === FIELD_VALUE_TYPE.BOOLEAN) {
      return renderBooleanValueCell(combinedData[currentDataRowIndex][column.code])
    }

    return combinedData[currentDataRowIndex][column.code]
  }

  return (
    <>
      {liProps['data-option-index'] === 0 ? (
        <DropDownListHeaders
          columns={columns}
          isLoading={isLoading}
          showSortOrder={showSortOrder}
          onUpdateRestrictions={handleUpdateRestrictions}
        />
      ) : null}
      <li
        {...liProps}
        style={{
          display: 'block',
          marginTop: 0,
          padding: 0,
          minWidth: MIN_AUTOCOMPLETE_POPPER_WIDTH,
        }}
      >
        <Grid
          container
          flexWrap='nowrap'
          sx={{
            m: 0,
            p: 0,
            height: '100%',
          }}
        >
          {columns.map(column => {
            return currentDataRowIndex !== -1 ? (
              <Grid
                item
                title={combinedData[currentDataRowIndex][column.code]}
                sx={{
                  p: '0 8px',
                  borderRight: `1px solid #303030`,
                  flex: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {renderValue(column)}
              </Grid>
            ) : (
              <></>
            )
          })}
        </Grid>
      </li>
    </>
  )
}
